/**
 * Formats the course display title by combining the base title with class title if available
 * @param {Object} params - Parameters for formatting the course display title
 * @param {string} params.displayTitle - Base course display title
 * @param {Object} params.selectedClass - Selected class object with optional class_title property
 * @returns {string} Formatted course display title
 */
export function formatCourseDisplayTitle({ displayTitle, selectedClass }) {
  if (selectedClass?.class_title) {
    return `${displayTitle}: ${selectedClass.class_title}`
  }
  return displayTitle
}

/**
 * Updates lesson summaries with lessonComplete field based on class instance data
 * @param {Array} lessonSummaries - Array of lesson summary objects
 * @param {Object} classInstanceData - Object containing lesson_nodes array
 * @returns {Array} Updated lesson summaries with lessonComplete field
 */
export function updateLessonCompleteness({
  lessonSummaries,
  classInstanceData,
}) {
  if (!Array.isArray(lessonSummaries) || !classInstanceData?.lesson_nodes) {
    return lessonSummaries || []
  }

  return lessonSummaries.map(summary => {
    const courseNodeEntryPoint =
      summary?.courseNodeConnector?.courseNodeEntryPoint

    if (!courseNodeEntryPoint) {
      return { ...summary, lessonComplete: false }
    }

    // Check if courseNodeEntryPoint is a substring of any leaf_node_id
    const isComplete = classInstanceData.lesson_nodes.some(lessonNode => {
      const leafNodeId = lessonNode?.leaf_node_id
      const isDone = lessonNode?.is_done

      return leafNodeId && leafNodeId.includes(courseNodeEntryPoint) && isDone
    })

    return { ...summary, lessonComplete: isComplete, isPreview: false }
  })
}

/**
 * Generates progress text based on URL and small group data
 * @param {Object} params - Parameters for generating progress text
 * @param {string} params.currentUrl - Current URL to check for tier-2/teach
 * @param {Object} params.ourClass - Small group data instance
 * @returns {string|undefined} Progress text or undefined if conditions not met
 */
export function generateProgressText({ currentUrl, ourClass }) {
  // Only show progress text if URL includes "tier-2/teach" not for preview
  if (!currentUrl.includes('tier-2/teach')) {
    return undefined
  }

  // Check if we have ourClass with lesson_nodes
  if (!ourClass?.lesson_nodes || !Array.isArray(ourClass.lesson_nodes)) {
    return undefined
  }

  // Count completed small groups
  const completedGroups = ourClass.lesson_nodes.filter(
    group => group.is_complete,
  )
  const totalGroups = ourClass.lesson_nodes.length

  if (totalGroups === 0) {
    return undefined
  }

  // Generate the progress text
  const lessonText = totalGroups === 1 ? 'Lesson' : 'Lessons'
  const progressText = `${completedGroups.length} ${lessonText} Complete`
  return progressText
}

/**
 * Finds the active header based on the current URL pathname
 * @param {Array} tabs - Array of tab objects with route, internalTitle, displayTitle, and description properties
 * @returns {Object|null} Active header object with id, title, and description, or null if no match found
 */
export function findActiveHeader(tabs) {
  const activeTab = tabs?.find(t => window.location.pathname.endsWith(t.route))

  if (!activeTab) {
    return null
  }

  return {
    id: activeTab.internalTitle || activeTab.displayTitle,
    title: activeTab.displayTitle,
    description: activeTab.description,
  }
}

/**
 * Prepares tab data for header tabs with active state
 * @param {Object} params - Parameters for preparing header tabs
 * @param {Array} params.tabSummaries - Array of tab summary objects
 * @param {Object} params.activeHeaderTab - Active header tab object with title property
 * @returns {Array} Array of header tabs with isActive property
 */
export function prepareDataForHeaderTabs({ tabSummaries, activeHeaderTab }) {
  return (
    tabSummaries?.map(tab => ({
      title: tab?.displayTitle,
      isActive: activeHeaderTab?.title === tab?.displayTitle,
      ...tab,
    })) || []
  )
}

/**
 * Prepares link summaries for the ShutterList component
 * @param {Array} linkSummaries - Array of link summary objects
 * @returns {Array} Array of items formatted for ShutterList component
 */
export function prepareItemsForShutterList(linkSummaries) {
  const itemList =
    linkSummaries?.map(item => {
      const tags = item?.metadata?.map(tag => {
        if (tag?.goal && typeof tag?.goal === 'string') {
          return tag?.goal.toUpperCase()
        }
        return null
      })

      return {
        title: item?.displayTitle,
        description: item?.description,
        path: item?.route,
        isPreview: item?.isPreview,
        markedComplete: item?.lessonComplete,
        tags: tags,
      }
    }) || []

  return itemList
}
